import { Schema } from 'prosemirror-model';

const schema = new Schema({
  nodes: {
    doc: { content: 'block+' },

    paragraph: { group: 'block', content: 'inline*' },

    text: { group: 'inline' },

    hardBreak: {
      inline: true,
      group: 'inline',
      selectable: false,
      parseDOM: [{ tag: 'br' }],
    },

    heading: {
      attrs: { level: { default: 1 } },
      content: 'inline*',
      group: 'block',
    },

    taskList: {
      content: 'taskListItem+',
      group: 'block',
      parseDOM: [{ tag: 'ul[data-task-list]' }],
      toDOM() {
        return ['ul', { 'data-task-list': '' }, 0];
      },
    },

    taskListItem: {
      content: 'paragraph taskList?',
      defining: true,
      attrs: { checked: { default: false } },
      parseDOM: [
        {
          tag: 'li[data-task-list-item]',
          getAttrs(dom) {
            return { checked: dom.getAttribute('data-checked') === 'true' };
          },
        },
      ],
      toDOM(node) {
        const attrs = { 'data-task-list-item': '' };
        if (node.attrs.checked) attrs['data-task-list-item'] = 'true';
        return ['li', attrs, 0];
      },
    },

    blockquote: { content: 'block+', group: 'block' },

    bulletList: { content: 'listItem+', group: 'block' },

    orderedList: {
      attrs: { order: { default: 1 } },
      content: 'listItem+',
      group: 'block',
    },

    listItem: { content: '(paragraph | orderedList | bulletList)*' },

    horizontalRule: { group: 'block' },

    image: {
      inline: true,
      attrs: { src: {}, alt: { default: null }, title: { default: null } },
      group: 'inline',
    },

    codeBlock: { content: 'text*', marks: '', group: 'block', code: true },

    table: {
      content: 'tableRow+',
      group: 'block',
      tableRole: 'table',
      attrs: {
        isControllersInjected: { default: false },
        insertButtonAttrs: { default: null },
      },
      parseDOM: [{ tag: 'table' }],
      toDOM(node) {
        return [
          'table',
          {
            'data-controllers-injected': node.attrs.isControllersInjected ? 'true' : null,
          },
          0,
        ];
      },
    },

    tableRow: {
      content: '(tableControllerCell | tableCell | tableHeaderCell)+',
      group: 'block',
      tableRole: 'row',
      parseDOM: [{ tag: 'tr' }],
      toDOM() {
        return ['tr', 0];
      },
    },

    tableControllerCell: {
      attrs: {
        colspan: { default: 1 },
        rowspan: { default: 1 },
        colwidth: { default: null },
        background: { default: null },
      },
      tableRole: 'controller_cell',
      parseDOM: [{ tag: 'td[data-controller-cell]' }],
      toDOM(node) {
        return ['td', { 'data-controller-cell': '' }, 0];
      },
    },

    tableCell: {
      content: 'block+',
      attrs: {
        colspan: { default: 1 },
        rowspan: { default: 1 },
        colwidth: { default: null },
        background: { default: null },
      },
      group: 'block',
      tableRole: 'cell',
      parseDOM: [{ tag: 'td' }],
      toDOM(node) {
        return ['td', 0];
      },
    },

    tableHeaderCell: {
      content: 'block+',
      attrs: {
        colspan: { default: 1 },
        rowspan: { default: 1 },
        colwidth: { default: null },
        background: { default: null },
      },
      group: 'block',
      tableRole: 'header_cell',
      parseDOM: [{ tag: 'th' }],
      toDOM(node) {
        return ['th', 0];
      },
    },

    iframe: {
      attrs: {
        src: { default: null },
        allowFullScreen: { default: 'true' },
        frameBorder: { default: 0 },
        type: { default: 'youtube' },
        width: { default: 560 },
        height: { default: 315 },
      },
      group: 'block',
      parseDOM: [
        {
          tag: 'iframe',
          getAttrs(dom) {
            return {
              src: dom.getAttribute('src'),
              allowFullScreen: dom.getAttribute('allowfullscreen'),
              frameBorder: dom.getAttribute('frameborder'),
              type: dom.getAttribute('data-type'),
              width: dom.getAttribute('width') || 560,
              height: dom.getAttribute('height') || 315,
            };
          },
        },
      ],
      toDOM(node) {
        return ['iframe', node.attrs];
      },
    },

    accordion: {
      content: 'accordionTitle accordionContent',
      group: 'block',
      isolating: true,
      defining: true,
      attrs: {
        open: { default: false },
      },
      parseDOM: [
        {
          tag: 'div[data-accordion]',
          getAttrs: (dom) => ({
            open: dom.getAttribute('data-open') === 'true',
          }),
        },
      ],
      toDOM: (node) => [
        'div',
        {
          'data-accordion': '',
          'data-open': node.attrs.open,
          class: 'accordion',
        },
        0,
      ],
    },

    accordionTitle: {
      content: 'inline*',
      group: 'block',
      defining: true,
      parseDOM: [{ tag: 'div[data-accordion-title]' }],
      toDOM: () => [
        'div',
        {
          'data-accordion-title': '',
          'data-accordion-placeholder': 'Give this expand a title...',
        },
        0,
      ],
    },

    accordionContent: {
      content: 'paragraph',
      group: 'block',
      defining: true,
      parseDOM: [{ tag: 'div[data-accordion-content]' }],
      toDOM: () => [
        'div',
        {
          'data-accordion-content': '',
          style: "--data-accordion-placeholder: 'Give this expand a content...'",
          'data-accordion-placeholder': 'Give this expand a content...',
        },
        0,
      ],
    },

    // Add the mention node
    mention: {
      inline: true,
      group: 'inline',
      atom: true,
      attrs: {
        id: { default: null },
        name: { default: null },
        avatar: { default: null },
        color: { default: null },
      },
      parseDOM: [
        {
          tag: 'span[data-mention]',
          getAttrs: (dom) => {
            const element = dom;
            return {
              id: element.getAttribute('data-mention-id'),
              name: element.getAttribute('data-mention-name'),
              avatar: element.getAttribute('data-mention-avatar'),
              color: element.getAttribute('data-mention-color'),
            };
          },
        },
      ],
      toDOM: (node) => {
        const { id, name, avatar, color } = node.attrs;
        return [
          'span',
          {
            'data-mention': '',
            'data-mention-id': id,
            'data-mention-name': name,
            'data-mention-avatar': avatar,
            'data-mention-color': color,
          },
          name,
        ];
      },
    },

    callout: {
      group: 'block',
      content: 'block+',
      defining: true,
      attrs: {
        type: { default: 'info' }, // info | warning | danger | success | note, etc.
      },
      parseDOM: [
        {
          tag: 'div[data-callout]',
          getAttrs(dom) {
            return {
              type: dom.getAttribute('data-callout-type') || 'info',
            };
          },
        },
      ],
      toDOM(node) {
        const { type } = node.attrs;
        return [
          'div',
          {
            'data-callout': '',
            'data-callout-type': type,
            class: `callout callout-${type}`,
          },
          0,
        ];
      },
    },
  },

  marks: {
    link: { attrs: { href: {}, title: { default: null } } },
    italic: {},
    em: {},
    bold: {},
    strong: {},
    code: {},
    strike: {},
    underline: {},
    superscript: {},
    subscript: {},
    sup: {},
    sub: {},
    textHighlight: {},
    textColor: {
      attrs: { color: { default: '#252103' } },
      parseDOM: [{ style: 'color', getAttrs: (value) => ({ color: value }) }],
      toDOM(mark) {
        return ['span', { style: `color: ${mark.attrs.color}` }, 0];
      },
    },
    fontSize: {
      attrs: {
        size: { default: '16px' },
      },
      parseDOM: [{ style: 'font-size', getAttrs: (value) => ({ size: value }) }],
      toDOM(mark) {
        return ['span', { style: `font-size: ${mark.attrs.size}` }, 0];
      },
    },
    textStyle: {
      attrs: { class: { default: 'date_class' } },
      toDOM: (node) => ['span', { class: node.attrs.class }, 0],
      parseDOM: [
        {
          tag: 'span',
          getAttrs: (dom) => ({
            class: dom.getAttribute('class') || '',
          }),
        },
      ],
    },
    inlineComment: {},
  },
});

export { schema };
export default schema;
